import { useCallback, useMemo, memo } from 'react';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import { getSelectedRowIds, setRowsSelected } from '../redux/selectionSlice';
import styled from 'styled-components';
import { SELECTION_COLUMN_WIDTH } from '../../table/constants/sizes';
import I18n from 'I18n';
import { Flex } from 'foundations-components/components/Flex';
import { useTrellisTheme } from '../../utils/useTrellisTheme';

// We cannot set legit borders because they do not work with sticky elements.
// Instead, we can create this div and position it one pixel outside its parent to
// simulate a border. We use this technique for the ResizeAffordance of the
// standard cells. Note: We don't need to apply position: sticky to this element
// because the parent is already sticky and its position is relative to the parent.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const RightBorder = styled.div.withConfig({
  displayName: "SelectAllCheckboxHeader__RightBorder",
  componentId: "o46kls-0"
})(["position:absolute;top:0;right:calc(", " * -0.5);height:100%;width:0;border-right:", " solid ", ";"], ({
  $theme
}) => $theme.space['25'], ({
  $theme
}) => $theme.space['25'], ({
  $theme
}) => $theme.color['border-core-default']);

// Using borders on a position: sticky element does not work, so we set the border
// using an inset box-shadow instead.
const CheckboxTH = styled.th.withConfig({
  displayName: "SelectAllCheckboxHeader__CheckboxTH",
  componentId: "o46kls-1"
})(["position:relative;height:100%;width:", "px;padding:", ";box-shadow:inset 0 -", " 0 ", ";&&{background-color:", ";}"], SELECTION_COLUMN_WIDTH, ({
  $theme
}) => $theme.space['0'], ({
  $theme
}) => $theme.space['25'], ({
  $theme
}) => $theme.color['border-core-default'], ({
  $theme
}) => $theme.color['fill-field-default']);
const StickyCheckboxTH = styled(CheckboxTH).withConfig({
  displayName: "SelectAllCheckboxHeader__StickyCheckboxTH",
  componentId: "o46kls-2"
})(["left:0;position:sticky;z-index:3;"]);
const SelectAllCheckboxHeader = ({
  numberOfFrozenColumns = 0,
  rowIds
}) => {
  const selectedRowIds = useTableSelector(getSelectedRowIds);
  const isCurrentPageSelected = useMemo(() => rowIds.length > 0 && rowIds.every(rowId => selectedRowIds.has(rowId)), [rowIds, selectedRowIds]);
  const dispatch = useTableDispatch();
  const handleChange = useCallback(({
    target: {
      checked
    }
  }) => {
    dispatch(setRowsSelected(rowIds.map(rowId => ({
      rowId,
      selected: checked
    }))));
  }, [dispatch, rowIds]);
  const Component = numberOfFrozenColumns > 0 ? StickyCheckboxTH : CheckboxTH;
  const theme = useTrellisTheme();
  return /*#__PURE__*/_jsxs(Component, {
    "data-test-id": "checkbox-select-all-container",
    $theme: theme,
    children: [/*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      flexGrow: 1,
      children: /*#__PURE__*/_jsx(Checkbox, {
        "aria-label": I18n.text('datatable.bulkActions.selectAllCheckboxLabel'),
        "data-test-id": "checkbox-select-all",
        checked: isCurrentPageSelected,
        indeterminate: selectedRowIds.size > 0,
        onChange: handleChange
      })
    }), /*#__PURE__*/_jsx(RightBorder, {
      $theme: theme
    })]
  });
};
export default /*#__PURE__*/memo(SelectAllCheckboxHeader);